import React from 'react'
import { Box, Typography, Paper, Stack } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import DevicesIcon from '@mui/icons-material/Devices'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import rgfoLogo from '../../../../assets/images/RockefellerGlobalFamilyOffice.png'

interface ConfirmationResult {
  date: string
  startTime: string
  meetingMode?: string
  message: string
}

interface ConfirmationProps {
  result: ConfirmationResult
}

const Confirmation: React.FC<ConfirmationProps> = ({ result }) => {
  const formatDate = (dateStr: string) =>
    new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    })

  const formatTime = (timeStr: string) => {
    const [h, m] = timeStr.split(':')
    const hour = Number.parseInt(h, 10)
    const period = hour >= 12 ? 'PM' : 'AM'
    let displayHour = hour % 12
    if (displayHour === 0) displayHour = 12
    return `${displayHour}:${m} ${period} ET`
  }

  return (
    <Paper
      elevation={0}
      sx={{
        overflow: 'hidden',
        borderRadius: '18px',
        bgcolor: '#FBFAF7',
        border: '1px solid #E7E4DB',
        boxShadow: '0 24px 60px -30px rgba(0,42,58,0.35)'
      }}
    >
      {/* Success header */}
      <Box sx={{ bgcolor: '#002A3A', px: { xs: 3, sm: 4 }, pt: 3.5, pb: 3.25 }}>
        <Box sx={{ textAlign: 'center', pb: 2.5, borderBottom: '1px solid rgba(180,157,119,0.28)' }}>
          <Box
            component="img"
            src={rgfoLogo}
            alt="Rockefeller Global Family Office"
            sx={{ display: 'inline-block', height: 34, width: 'auto', maxWidth: '100%', filter: 'brightness(0) invert(1)' }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.25, mt: 2.75 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 40, color: '#4FA88F', flexShrink: 0 }} />
          <Box>
            <Typography sx={{ color: '#F3F1EC', fontWeight: 600, fontSize: '1.5rem', lineHeight: 1.1, letterSpacing: '-0.01em' }}>
              Booking Confirmed
            </Typography>
            <Typography sx={{ color: 'rgba(243,241,236,0.78)', fontWeight: 300, fontSize: '0.9rem', lineHeight: 1.5, mt: 0.75 }}>
              {result.message}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Meeting details */}
      <Box sx={{ px: { xs: 3, sm: 4 }, py: 3.5 }}>
        <Typography
          sx={{ color: '#84744D', fontWeight: 600, fontSize: '0.75rem', letterSpacing: '0.16em', textTransform: 'uppercase', display: 'block', mb: 2 }}
        >
          Meeting Details
        </Typography>

        <Box>
          <DetailRow
            icon={<CalendarMonthIcon sx={{ fontSize: 22, color: '#002A3A' }} />}
            label="Date"
            value={formatDate(result.date)}
          />
          <DetailRow
            icon={<AccessTimeIcon sx={{ fontSize: 22, color: '#002A3A' }} />}
            label="Time"
            value={formatTime(result.startTime)}
            last={!result.meetingMode}
          />
          {result.meetingMode && (
            <DetailRow
              icon={<DevicesIcon sx={{ fontSize: 22, color: '#002A3A' }} />}
              label="Format"
              value={result.meetingMode}
              last
            />
          )}
        </Box>
      </Box>

      <Box sx={{ height: 1, bgcolor: '#ECE9E0', mx: { xs: 3, sm: 4 } }} />

      {/* What's next */}
      <Box sx={{ px: { xs: 3, sm: 4 }, py: 3.5 }}>
        <Typography
          sx={{ color: '#84744D', fontWeight: 600, fontSize: '0.75rem', letterSpacing: '0.16em', textTransform: 'uppercase', display: 'block', mb: 2.5 }}
        >
          What Happens Next
        </Typography>

        <Stack spacing={2.75}>
          <NextStep
            icon={<EmailOutlinedIcon sx={{ fontSize: 21, color: '#007680' }} />}
            title="Calendar invite sent"
            desc="A meeting invite will arrive in your inbox shortly."
          />
          <NextStep
            icon={<DescriptionOutlinedIcon sx={{ fontSize: 21, color: '#B49D77' }} />}
            title="Advisory team prepares"
            desc="Your team will prepare relevant materials for your review."
          />
          <NextStep
            icon={<EventOutlinedIcon sx={{ fontSize: 21, color: '#002A3A' }} />}
            title="Your meeting"
            desc={`Join your advisors on ${new Date(result.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}.`}
          />
        </Stack>

        <Box
          sx={{
            mt: 3,
            display: 'flex',
            gap: 1.5,
            alignItems: 'flex-start',
            bgcolor: '#F1EFE8',
            border: '1px solid #E7E4DB',
            borderRadius: '12px',
            px: 2.25,
            py: 1.75
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 20, color: '#84744D', flexShrink: 0, mt: 0.2 }} />
          <Typography sx={{ fontSize: '0.85rem', color: '#5A6567', lineHeight: 1.5 }}>
            Need to{' '}
            <Box component="span" sx={{ fontWeight: 600, color: '#002A3A' }}>
              reschedule
            </Box>
            ? Use the same link from your email. To{' '}
            <Box component="span" sx={{ fontWeight: 600, color: '#002A3A' }}>
              cancel
            </Box>
            , contact your advisory team.
          </Typography>
        </Box>
      </Box>
    </Paper>
  )
}

function DetailRow({
  icon,
  label,
  value,
  last = false
}: Readonly<{ icon: React.ReactNode; label: string; value: string; last?: boolean }>) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        py: 2,
        borderBottom: last ? 'none' : '1px solid #ECE9E0'
      }}
    >
      {icon}
      <Box>
        <Typography
          sx={{ color: '#9AA0A0', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.9, fontWeight: 500 }}
        >
          {label}
        </Typography>
        <Typography sx={{ fontWeight: 600, fontSize: '1.05rem', color: '#002A3A', mt: 0.25 }}>{value}</Typography>
      </Box>
    </Box>
  )
}

function NextStep({ icon, title, desc }: Readonly<{ icon: React.ReactNode; title: string; desc: string }>) {
  return (
    <Box sx={{ display: 'flex', gap: 1.9, alignItems: 'flex-start' }}>
      <Box sx={{ mt: 0.25 }}>{icon}</Box>
      <Box>
        <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#002A3A', lineHeight: 1.3 }}>{title}</Typography>
        <Typography sx={{ fontWeight: 300, fontSize: '0.9rem', color: '#5A6567', lineHeight: 1.55, mt: 0.4 }}>
          {desc}
        </Typography>
      </Box>
    </Box>
  )
}

export default Confirmation
